import axios from "./axios";
import { ApiResponse } from "../types/express-data.type";
import { BdcListShape } from "../types/bdc.type";
import {
  BdcBranch,
  BdcDashboardStores,
  BdcDashboardStoresFilter,
  BdcDashboardStoresParams,
  BdcDashboardSummary,
  BdcDashboardSummaryParams,
  BdcStoresExportScope,
} from "../types/bdcDashboard.type";

// ---- BDC GM dashboard — /bdc/dashboard ----
// A 403 here is a scope answer (BDC_FORBIDDEN / BDC_BRANCH_OUT_OF_SCOPE) and a 422 means no
// BDC branch is registered yet: the screen shows the server's Thai message inline, so these
// calls skip both the Permission Denied redirect and the global toast.

const INLINE_ERRORS = { suppressForbiddenRedirect: true, suppressErrorToast: true } as const;

export const buildBdcDashboardSummaryQuery = (params: BdcDashboardSummaryParams) => {
  const query = new URLSearchParams();
  query.append("date", params.date);
  query.append("granularity", params.granularity);
  if (params.dc_code) query.append("dc_code", params.dc_code);
  if (params.company_id) query.append("company_id", params.company_id);
  return query;
};

export const buildBdcDashboardStoresQuery = (filter: BdcDashboardStoresFilter) => {
  const query = new URLSearchParams();
  query.append("date", filter.date);
  if (filter.level) query.append("level", filter.level);
  if (filter.dc_code) query.append("dc_code", filter.dc_code);
  if (filter.line_no !== undefined) query.append("line_no", String(filter.line_no));
  if (filter.q) query.append("q", filter.q);
  if (filter.company_id) query.append("company_id", filter.company_id);
  if (filter.cells !== undefined) query.append("cells", String(filter.cells));
  return query;
};

export const getBdcDashboardSummary = (params: BdcDashboardSummaryParams) =>
  axios.get<ApiResponse<BdcDashboardSummary>>(
    `/bdc/dashboard/summary?${buildBdcDashboardSummaryQuery(params).toString()}`,
    { meta: INLINE_ERRORS },
  );

export const getBdcDashboardStores = (params: BdcDashboardStoresParams) => {
  const query = buildBdcDashboardStoresQuery(params);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  return axios.get<ApiResponse<BdcDashboardStores>>(`/bdc/dashboard/stores?${query.toString()}`, {
    meta: INLINE_ERRORS,
  });
};

/**
 * `scope=all` exports every store of the base (ปกติ and ข้อมูลยังน้อย too), ignoring `level`.
 * A blob's error body can't be read by the interceptor, so the toast text is set per status.
 */
export const exportBdcDashboardStores = (filter: BdcDashboardStoresFilter, scope: BdcStoresExportScope = "lapsed") => {
  const query = buildBdcDashboardStoresQuery(scope === "all" ? { ...filter, level: undefined } : filter);
  if (scope === "all") query.append("scope", scope);
  return axios.get<Blob>(`/bdc/dashboard/stores/export?${query.toString()}`, {
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        403: "ไม่มีสิทธิ์ดูข้อมูลของสาขา BDC นี้",
        422: "Export ไม่สำเร็จ — ตรวจตัวกรอง หรือรายการมากเกินกว่าจะ Export ได้",
      },
    },
  });
};

/** The BDC branches the caller may see; HO / MASTER get every registered one. */
export const listBdcDashboardBranches = () =>
  axios.get<ApiResponse<BdcListShape<BdcBranch>>>(`/bdc/branches`, { meta: INLINE_ERRORS });
